import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bell, X, AlertTriangle, TrendingUp, Shield, CheckCircle, Clock } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Only poll when the user is actually authenticated. This avoids the
    // "TypeError: Failed to fetch" noise on logout / login screens / 401-expired
    // sessions.
    const token = localStorage.getItem('token');
    if (!token) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;  // session expired between mount and poll — bail quietly
    try {
      const safeFetch = async (path) => {
        try {
          const r = await fetch(`${API_URL}${path}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!r.ok) return [];
          return await r.json();
        } catch {
          return [];  // network error — bail quietly, don't pollute the console
        }
      };

      // Fetch anomalies as notifications
      const anomalies = await safeFetch('/api/anomalies');
      // Fetch guardrails for budget alerts
      const guardrails = await safeFetch('/api/guardrails');
      
      // Convert to notifications
      const notifs = [];
      
      // Add anomaly notifications
      anomalies.slice(0, 3).forEach((a, i) => {
        notifs.push({
          id: `anomaly-${i}`,
          type: 'anomaly',
          title: 'Cost Anomaly Detected',
          message: `${a.service}: ${a.description || 'Unusual cost pattern detected'}`,
          time: a.detected_at || new Date().toISOString(),
          read: false,
          icon: AlertTriangle,
          color: 'text-amber-500',
          bgColor: 'bg-amber-50 dark:bg-amber-900/20'
        });
      });
      
      // Add budget alert notifications
      guardrails.filter(g => g.utilization > 80).forEach((g, i) => {
        notifs.push({
          id: `budget-${i}`,
          type: 'budget',
          title: 'Budget Alert',
          message: `${g.name}: ${g.utilization?.toFixed(1)}% of budget used`,
          time: new Date().toISOString(),
          read: false,
          icon: Shield,
          color: g.utilization > 90 ? 'text-rose-500' : 'text-amber-500',
          bgColor: g.utilization > 90 ? 'bg-rose-50 dark:bg-rose-900/20' : 'bg-amber-50 dark:bg-amber-900/20'
        });
      });
      
      // Add a savings opportunity notification
      notifs.push({
        id: 'savings-tip',
        type: 'tip',
        title: 'Savings Opportunity',
        message: 'Schedule non-prod resources to save up to 65% on idle costs',
        time: new Date().toISOString(),
        read: true,
        icon: TrendingUp,
        color: 'text-emerald-500',
        bgColor: 'bg-emerald-50 dark:bg-emerald-900/20'
      });
      
      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => !n.read).length);
    } catch (error) {
      // safeFetch already swallows network errors — anything that reaches here
      // is a logic bug. Log without polluting the user-facing console.
      if (process.env.NODE_ENV === 'development') {
        console.warn('NotificationBell render bug:', error);
      }
    }
  };

  const markAsRead = (id) => {
    setNotifications(notifications.map(n => 
      n.id === id ? { ...n, read: true } : n
    ));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const clearNotification = (id) => {
    setNotifications(notifications.filter(n => n.id !== id));
    const notif = notifications.find(n => n.id === id);
    if (notif && !notif.read) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative rounded-lg h-9 w-9"
          data-testid="notification-bell"
          aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
          title={unreadCount > 0 ? `${unreadCount} unread notifications` : 'Notifications'}
        >
          <Bell className="w-4 h-4 text-slate-400" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-rose-500 text-white text-[11px] font-bold rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <h3 className="font-semibold text-sm">Notifications</h3>
          {unreadCount > 0 && (
            <button 
              onClick={markAllAsRead}
              className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
            >
              Mark all read
            </button>
          )}
        </div>
        
        {/* Notifications List */}
        <div className="max-h-[400px] overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="py-8 text-center text-slate-400">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">You're all caught up!</p>
            </div>
          ) : (
            notifications.map((notif) => (
              <div 
                key={notif.id}
                className={`relative px-4 py-3 border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors ${
                  !notif.read ? 'bg-slate-50/50 dark:bg-slate-800' : ''
                }`}
                onClick={() => markAsRead(notif.id)}
              >
                <div className="flex gap-3">
                  <div className={`w-8 h-8 rounded-full ${notif.bgColor} flex items-center justify-center flex-shrink-0`}>
                    <notif.icon className={`w-4 h-4 ${notif.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm font-medium ${!notif.read ? 'text-slate-900 dark:text-white' : 'text-slate-400 dark:text-slate-400'}`}>
                        {notif.title}
                      </p>
                      <button 
                        onClick={(e) => { e.stopPropagation(); clearNotification(notif.id); }}
                        className="text-slate-400 hover:text-slate-400 p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{notif.message}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Clock className="w-3 h-3 text-slate-400" />
                      <span className="text-[11px] text-slate-400">{formatTime(notif.time)}</span>
                      {!notif.read && (
                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        
        {/* Footer */}
        {notifications.length > 0 && (
          <div className="px-4 py-2 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
            <Link
              to="/notifications"
              onClick={() => setIsOpen(false)}
              className="block text-xs text-emerald-600 hover:text-emerald-700 font-medium text-center"
              data-testid="notification-view-all"
            >
              View all notifications →
            </Link>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
